// platform.js — browser-native API abstraction layer
// All browser globals are isolated here so the Capacitor sprint can swap
// each export to a plugin call without touching App.js.

import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import * as Sentry from './errorReporting';

// Network
export const isOnline = () => navigator.onLine;
export const addNetworkListener = (type, handler) => window.addEventListener(type, handler);
export const removeNetworkListener = (type, handler) => window.removeEventListener(type, handler);

// Global error hooks (onboarding pre-consent crash beacon, spec D9). Returns a remover.
export function addGlobalErrorListener(handler) {
  const onError = (e) => handler(e && e.error ? e.error : new Error('window.error'));
  const onRejection = (e) => handler(e && e.reason instanceof Error ? e.reason : new Error('unhandledrejection'));
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => { window.removeEventListener('error', onError); window.removeEventListener('unhandledrejection', onRejection); };
}

// Another document (web tab) wrote `key` in localStorage; e.key is null when it cleared everything. The
// browser fires `storage` only in other documents, never the writer. Returns a remover. (#1464 Codex r1)
export function addStorageListener(key, handler) {
  const onStorage = (e) => { if (!e || e.key === key || e.key === null) handler(); };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

// External links
export const openExternal = (url) => window.open(url, '_blank');

// In-app browser for tapped article links (The Wire, spec 2026-08-09-news-feed.md
// §PR 3). Native: Capacitor Browser (in-app Safari view). Web: new tab.
// SECURITY (Security L2 carry-forward): fail closed — only http(s) URLs are ever
// opened. A non-string, unparseable, or non-http(s) scheme (javascript:, data:,
// file:, …) is dropped silently so a poisoned news row can never drive navigation.
export async function openInAppBrowser(url) {
  if (typeof url !== 'string') return;
  let scheme;
  try {
    scheme = new URL(url).protocol;
  } catch {
    return; // unparseable — fail closed
  }
  if (scheme !== 'http:' && scheme !== 'https:') return;
  try {
    if (isCapacitorNative()) {
      // Lazy-load the native plugin: @capacitor/browser calls registerPlugin at
      // import time, which throws under a minimally-mocked @capacitor/core. Deferring
      // it to the native branch keeps platform.js loadable in web/test contexts that
      // never open an in-app browser.
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
    } else {
      openExternal(url);
    }
  } catch (err) {
    Sentry.captureException(err, { tags: { function: 'openInAppBrowser' } });
  }
}

// OAuth origin
export const getOrigin = () => window.location.origin;

// Force reload (service worker update)
export const reloadApp = (url) => { window.location.href = url; };

// Current pathname
export const getPathname = () => window.location.pathname;

// Clipboard — write
export const copyText = async (text) => { await navigator.clipboard.writeText(text); };

// Clipboard — read image (handlePaste, preserved for Capacitor sprint)
export const readClipboardImage = async () => { return await navigator.clipboard.read(); };

// DOM — body class manipulation (Capacitor swap point: safe-area / scroll-lock utilities)
export const addBodyClass = (className) => document.body.classList.add(className);
export const removeBodyClass = (className) => document.body.classList.remove(className);

// Blob URLs (Capacitor swap point: native file preview / temp-file manager)
export const createObjectURL = (blob) => URL.createObjectURL(blob);
export const revokeObjectURL = (url) => URL.revokeObjectURL(url);

// File I/O (Capacitor swap point: native filesystem / camera plugin)
export const createFileReader = () => new FileReader();
export const createFile = (parts, name, opts) => new File(parts, name, opts);

// Device detection (Capacitor swap point: Device.getInfo())
export const getUserAgent = () => navigator.userAgent;

// UUID — sole owner of crypto.randomUUID (#915 idempotency keys). Never call crypto.*
// inline in a component/hook. Falls back to a v4-shaped string on very old WebViews.
export function randomUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Nonce helpers for native Sign in with Apple (#1002). platform.js is the sole owner of crypto.*.
// SECURITY: these MUST fail closed — a weak nonce breaks the Apple/Supabase replay protection,
// so unlike randomUUID there is deliberately NO Math.random fallback. See spec §2.
export function generateNonce() {
  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
    throw new Error('generateNonce: crypto.getRandomValues unavailable — refusing to produce an insecure nonce');
  }
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export async function sha256Hex(input) {
  if (typeof crypto === 'undefined' || !crypto.subtle || typeof crypto.subtle.digest !== 'function') {
    throw new Error('sha256Hex: crypto.subtle.digest unavailable — cannot hash nonce');
  }
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

// Capacitor native runtime detection
export const isCapacitorNative = () => Capacitor.isNativePlatform();

// Photo library picker (Capacitor Camera plugin)
export async function pickPhotoFromLibrary() {
  const photo = await Camera.getPhoto({
    source: CameraSource.Photos,
    resultType: CameraResultType.DataUrl,
    quality: 90,
    width: 1800,
    height: 1800,
  });
  return photo;
}

// Reduced-motion preference. platform.js is the sole owner of window.* access.
// Read live (not cached) so an OS-level change is honored on next render.
export function prefersReducedMotion() {
  try {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  } catch {
    // Recoverable/degraded: fall back to false. Not a captureException per CC-IMPL-037 —
    // this is a transient environment condition, not a terminal failure.
    Sentry.captureMessage("prefersReducedMotion: matchMedia unavailable", "warning");
    return false;
  }
}

// Local key/value storage. platform.js is the sole owner of window.* access, so
// any module that needs to remember a preference goes through these two helpers
// instead of touching localStorage itself. Failures (private mode, quota,
// storage disabled) are recoverable: the caller falls back to its default, so
// report at warning level per CC-IMPL-037 -- never captureException.
export function storageGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    Sentry.captureMessage(`storageGet: localStorage unavailable (${key})`, "warning");
    return null;
  }
}

export function storageSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    Sentry.captureMessage(`storageSet: localStorage unavailable (${key})`, "warning");
    return false;
  }
}
